import React from 'react';
import { Mic, Upload } from 'lucide-react';

interface BootstrapDropzoneProps {
    allowRecording: boolean;
    hint: string;
    onClick: () => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    onRecordClick: () => void;
    /** Denser padding/sizing for narrow widgets/sidebars. Default false. */
    compact?: boolean;
}

// Ported from InstantVoiceClone.tsx's upload/drop area (previously inlined in the page).
export default function BootstrapDropzone({ allowRecording, hint, onClick, onDragOver, onDrop, onRecordClick, compact = false }: BootstrapDropzoneProps) {
    const iconSize = compact ? 36 : 64;
    return (
        <div
            role="button"
            tabIndex={0}
            aria-label="Upload an audio file"
            style={{
                border: '2px dashed rgba(var(--bs-primary-rgb), 0.25)',
                borderRadius: compact ? '12px' : '16px',
                padding: compact ? '16px 14px' : '48px 24px',
                textAlign: 'center',
                background: 'rgba(var(--bs-primary-rgb), 0.02)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
            }}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.5)';
                e.currentTarget.style.background = 'rgba(var(--bs-primary-rgb), 0.04)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(var(--bs-primary-rgb), 0.25)';
                e.currentTarget.style.background = 'rgba(var(--bs-primary-rgb), 0.02)';
            }}
        >
            <div style={{
                width: iconSize,
                height: iconSize,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: compact ? '0 auto 10px' : '0 auto 20px',
                boxShadow: '0 6px 20px rgba(var(--bs-primary-rgb), 0.25)',
            }}>
                <Upload size={compact ? 16 : 26} color="white" />
            </div>
            <h5 style={{ fontSize: compact ? '13px' : '17px', fontWeight: 700, color: 'var(--bs-emphasis-color)', marginBottom: compact ? 4 : 8 }}>
                Upload your audio file
            </h5>
            <p style={{ fontSize: compact ? '11px' : '13px', color: 'var(--bs-secondary-color)', marginBottom: compact ? 12 : 24 }}>
                {compact ? hint : (<>Drop your file here or click to browse<br /><span style={{ fontSize: '12px' }}>{hint}</span></>)}
            </p>

            {allowRecording && (
                <>
                    <div className="d-flex align-items-center justify-content-center gap-3" style={{ marginBottom: compact ? 10 : 20 }}>
                        <div style={{ height: 1, width: compact ? 36 : 60, background: 'rgba(var(--bs-primary-rgb), 0.15)' }} />
                        <span style={{ fontSize: compact ? '10px' : '12px', color: 'var(--bs-secondary-color)', fontWeight: 600, letterSpacing: '0.5px' }}>OR</span>
                        <div style={{ height: 1, width: compact ? 36 : 60, background: 'rgba(var(--bs-primary-rgb), 0.15)' }} />
                    </div>

                    <button
                        className="btn btn-sm fw-semibold"
                        onClick={(e) => { e.stopPropagation(); onRecordClick(); }}
                        style={{
                            background: 'linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary) 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: compact ? '8px' : '10px',
                            padding: compact ? '6px 14px' : '9px 22px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: compact ? 5 : 7,
                            fontSize: compact ? '12px' : '13px',
                            boxShadow: '0 4px 14px rgba(var(--bs-primary-rgb), 0.30)',
                        }}
                    >
                        <Mic size={compact ? 12 : 15} />
                        Record Now
                    </button>
                </>
            )}
        </div>
    );
}
